import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import ReportLayout, { ReportTable, usePagination } from './ReportLayout';
import { ReportChartRow, BarCard, DonutCard, groupCount, asSeries } from './ReportCharts';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { useShiftTypes } from '../../hooks/useSettings';

const COLS = [
  { key:'staff_name',       label:'Staff Name' },
  { key:'compliance',       label:'Complian...', render: v => (
    <span className={clsx('font-semibold text-sm', v==='Over Cap' ? 'text-red-600' : v==='Near Cap' ? 'text-amber-500' : 'text-green-600')}>
      {v}
    </span>
  )},
  { key:'max_hours',        label:'Max Hour...', render: v => parseFloat(v)?.toFixed(0) },
  { key:'hours_worked',     label:'Hours Wo...' },
  { key:'variance',         label:'Variance', render: v => (
    <span className={clsx('font-medium', v > 0 ? 'text-red-500' : v < 0 ? 'text-green-600' : 'text-slate-400')}>{v}</span>
  )},
  { key:'period_type',      label:'Period Type' },
  { key:'enforcement',      label:'Enforcem...' },
  { key:'period_start',     label:'Period Start', render: v => v ? format(new Date(v), 'dd/MM/yyyy') : '–' },
  { key:'period_end',       label:'Period End',   render: v => v ? format(new Date(v), 'dd/MM/yyyy') : '–' },
  { key:'_team',            label:'Team',         render: () => '–' },
  { key:'pay_group',        label:'Pay Group' },
  { key:'role',             label:'Role', render: v => <span className="capitalize">{v||'–'}</span> },
  { key:'employment_type',  label:'Employm...' },
];

export default function WorkHoursCompliance() {
  const [data, setData]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [viewTab, setViewTab]     = useState('staff');
  const [date, setDate]           = useState(format(new Date(), 'yyyy-MM-dd'));
  const [shiftType, setShiftType] = useState('');
  const shiftTypes = useShiftTypes();
  const pagination = usePagination(data);

  const TAB_PERIOD = { staff: 'daily', daily: 'daily', monthly: 'monthly' };
  const periodType = TAB_PERIOD[viewTab] || 'daily';

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ period_type: periodType, date });
    if (shiftType) p.set('shift_type', shiftType);
    api.get(`/reports/work-hours-compliance?${p}`)
      .then(r => { setData(r.data || []); pagination.reset(); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [periodType, date, shiftType]);

  useEffect(() => { load(); }, [load]);

  const chips = [
    { label:'Period', value:periodType },
    { label:'Date', value:date },
    ...(shiftType ? [{ label:'Shift Type', value: shiftType, onRemove: () => setShiftType('') }] : []),
  ];

  return (
    <ReportLayout title="Work Hours Compliance - Staff View" data={data} filename="work_hours_compliance"
      columns={COLS} activeChips={chips} showFiltersPanel={showFilters} onToggleFilters={() => setShowFilters(v=>!v)}
      chart={
        <ReportChartRow>
          <DonutCard title="Compliance status" data={groupCount(data, 'compliance')} />
          <BarCard title="Most over cap (hrs)" data={asSeries(data, 'staff_name', 'variance', { limit: 8 })} color="#EF4444" horizontal />
        </ReportChartRow>
      }
      filters={
        <div className="flex flex-wrap gap-4">
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Date</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} className="input-field !w-auto text-sm"/></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Shift Type</label>
            <select value={shiftType} onChange={e => setShiftType(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Types</option>
              {shiftTypes.filter(t => t.is_active).map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
            </select></div>
          <div className="flex items-end"><button onClick={load} className="btn-primary text-sm px-4 py-2">Apply</button></div>
        </div>
      }>
      {/* View tabs */}
      <div className="flex gap-1 mb-4">
        {['staff','daily','monthly'].map(t => (
          <button key={t} onClick={() => setViewTab(t)}
            className={clsx('px-4 py-2 rounded-lg text-sm font-medium capitalize transition-all border',
              viewTab===t ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50')}>
            {t}
          </button>
        ))}
      </div>
      <ReportTable columns={COLS} data={data} loading={loading} pagination={pagination}/>
    </ReportLayout>
  );
}
